import React, { useState, useRef, useEffect } from 'react'
import { Mic, MicOff, Play, Pause, Square, Upload, Loader2 } from 'lucide-react'

interface VoiceRecorderProps {
  onStandupSubmitted: (data: any) => void
  userId: string
  teamId: string
  supabaseUrl: string
}

interface RecordingState {
  isRecording: boolean
  isPaused: boolean
  isPlaying: boolean
  isProcessing: boolean
  duration: number
  audioBlob: Blob | null
  audioUrl: string | null
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onStandupSubmitted,
  userId,
  teamId,
  supabaseUrl
}) => {
  const [recordingState, setRecordingState] = useState<RecordingState>({
    isRecording: false,
    isPaused: false,
    isPlaying: false,
    isProcessing: false,
    duration: 0,
    audioBlob: null,
    audioUrl: null
  })

  const [error, setError] = useState<string | null>(null)
  const [transcript, setTranscript] = useState<string>('')
  const [standupData, setStandupData] = useState<any>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  // Cleanup function
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
      }
      if (recordingState.audioUrl) {
        URL.revokeObjectURL(recordingState.audioUrl)
      }
    }
  }, [recordingState.audioUrl])

  const startRecording = async () => {
    try {
      setError(null)
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      })
      
      streamRef.current = stream
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      })
      
      mediaRecorderRef.current = mediaRecorder
      
      const chunks: Blob[] = []
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data)
        }
      }
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(chunks, { type: 'audio/webm;codecs=opus' })
        const audioUrl = URL.createObjectURL(audioBlob)
        
        setRecordingState(prev => ({
          ...prev,
          audioBlob,
          audioUrl,
          isRecording: false,
          isPaused: false
        }))
      }
      
      mediaRecorder.start()
      
      setRecordingState(prev => ({
        ...prev,
        isRecording: true,
        duration: 0
      }))
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingState(prev => ({
          ...prev,
          duration: prev.duration + 1
        }))
      }, 1000)
      
    } catch (err) {
      console.error('Error starting recording:', err)
      setError('Failed to start recording. Please check microphone permissions.')
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && recordingState.isRecording) {
      mediaRecorderRef.current.stop()
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
      }
      
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }

  const pauseRecording = () => {
    if (mediaRecorderRef.current && recordingState.isRecording) {
      mediaRecorderRef.current.pause()
      setRecordingState(prev => ({ ...prev, isPaused: true }))
      
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }

  const resumeRecording = () => {
    if (mediaRecorderRef.current && recordingState.isPaused) {
      mediaRecorderRef.current.resume()
      setRecordingState(prev => ({ ...prev, isPaused: false }))
      
      // Resume timer
      timerRef.current = setInterval(() => {
        setRecordingState(prev => ({
          ...prev,
          duration: prev.duration + 1
        }))
      }, 1000)
    }
  }

  const playRecording = () => {
    if (recordingState.audioUrl && audioRef.current) {
      audioRef.current.play()
      setRecordingState(prev => ({ ...prev, isPlaying: true }))
    }
  }

  const pausePlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      setRecordingState(prev => ({ ...prev, isPlaying: false }))
    }
  }

  const resetRecording = () => {
    if (recordingState.audioUrl) {
      URL.revokeObjectURL(recordingState.audioUrl)
    }
    
    setRecordingState({
      isRecording: false,
      isPaused: false,
      isPlaying: false,
      isProcessing: false,
      duration: 0,
      audioBlob: null,
      audioUrl: null
    })
    
    setTranscript('')
    setStandupData(null)
    setError(null)
  }

  const submitVoiceStandup = async () => {
    if (!recordingState.audioBlob) {
      setError('No recording to submit')
      return
    }

    setRecordingState(prev => ({ ...prev, isProcessing: true }))
    setError(null)

    try {
      const formData = new FormData()
      formData.append('audio', recordingState.audioBlob, 'standup.webm')
      formData.append('userId', userId)
      formData.append('teamId', teamId)

      const response = await fetch(`${supabaseUrl}/functions/v1/voice-standup-processor`, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Processing failed: ${errorText}`)
      }

      const result = await response.json()
      
      setTranscript(result.transcript)
      setStandupData(result.standupData)
      
      // Notify parent component
      onStandupSubmitted(result.standupData)
      
    } catch (err) {
      console.error('Error submitting voice standup:', err)
      setError(err instanceof Error ? err.message : 'Failed to process voice standup')
    } finally {
      setRecordingState(prev => ({ ...prev, isProcessing: false }))
    }
  }

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Voice Standup</h3>
        <div className="text-sm text-gray-500">
          {recordingState.duration > 0 && formatDuration(recordingState.duration)}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Recording Controls */}
      <div className="flex items-center space-x-4 mb-6">
        {!recordingState.isRecording && !recordingState.audioBlob && (
          <button
            onClick={startRecording}
            className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            <Mic className="w-4 h-4" />
            <span>Start Recording</span>
          </button>
        )}

        {recordingState.isRecording && !recordingState.isPaused && (
          <>
            <button
              onClick={pauseRecording}
              className="flex items-center space-x-2 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
            >
              <Pause className="w-4 h-4" />
              <span>Pause</span>
            </button>
            <button
              onClick={stopRecording}
              className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <Square className="w-4 h-4" />
              <span>Stop</span>
            </button>
          </>
        )}

        {recordingState.isPaused && (
          <>
            <button
              onClick={resumeRecording}
              className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              <Mic className="w-4 h-4" />
              <span>Resume</span>
            </button>
            <button
              onClick={stopRecording}
              className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <Square className="w-4 h-4" />
              <span>Stop</span>
            </button>
          </>
        )}

        {recordingState.audioBlob && !recordingState.isRecording && (
          <>
            <button
              onClick={recordingState.isPlaying ? pausePlayback : playRecording}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {recordingState.isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              <span>{recordingState.isPlaying ? 'Pause' : 'Play'}</span>
            </button>
            <button
              onClick={resetRecording}
              className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <MicOff className="w-4 h-4" />
              <span>Reset</span>
            </button>
            <button
              onClick={submitVoiceStandup}
              disabled={recordingState.isProcessing}
              className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {recordingState.isProcessing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              <span>{recordingState.isProcessing ? 'Processing...' : 'Submit Standup'}</span>
            </button>
          </>
        )}
      </div>

      {/* Audio Player */}
      {recordingState.audioUrl && (
        <audio
          ref={audioRef}
          src={recordingState.audioUrl}
          onEnded={() => setRecordingState(prev => ({ ...prev, isPlaying: false }))}
          className="hidden"
        />
      )}

      {/* Recording Indicator */}
      {recordingState.isRecording && (
        <div className="flex items-center space-x-2 mb-4">
          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
          <span className="text-sm text-gray-600">
            {recordingState.isPaused ? 'Recording paused' : 'Recording...'}
          </span>
        </div>
      )}

      {/* Transcript Display */}
      {transcript && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Transcript:</h4>
          <p className="text-sm text-gray-700">{transcript}</p>
        </div>
      )}

      {/* Processed Standup Data */}
      {standupData && (
        <div className="mt-6 space-y-4">
          <h4 className="text-sm font-medium text-gray-900">Extracted Standup Information:</h4>
          
          <div className="space-y-3">
            <div className="p-3 bg-blue-50 rounded-lg">
              <h5 className="text-sm font-medium text-blue-900">Yesterday:</h5>
              <p className="text-sm text-blue-800">{standupData.questions?.yesterday}</p>
            </div>
            
            <div className="p-3 bg-green-50 rounded-lg">
              <h5 className="text-sm font-medium text-green-900">Today:</h5>
              <p className="text-sm text-green-800">{standupData.questions?.today}</p>
            </div>
            
            <div className="p-3 bg-yellow-50 rounded-lg">
              <h5 className="text-sm font-medium text-yellow-900">Blockers:</h5>
              <p className="text-sm text-yellow-800">{standupData.questions?.blockers}</p>
            </div>
          </div>

          {standupData.summary && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <h5 className="text-sm font-medium text-gray-900">Summary:</h5>
              <p className="text-sm text-gray-700">{standupData.summary}</p>
            </div>
          )}

          <div className="flex items-center space-x-4 text-xs text-gray-500">
            <span>Sentiment: {standupData.sentiment}</span>
            <span>Confidence: {Math.round((standupData.confidence || 0) * 100)}%</span>
          </div>
        </div>
      )}

      {/* Instructions */}
      {!recordingState.isRecording && !recordingState.audioBlob && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="text-sm font-medium text-blue-900 mb-2">How to use Voice Standup:</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Click "Start Recording" and speak naturally</li>
            <li>• Mention what you did yesterday, plan for today, and any blockers</li>
            <li>• You can pause/resume during recording</li>
            <li>• Review your recording before submitting</li>
            <li>• AI will extract and structure your standup information</li>
          </ul>
        </div>
      )}
    </div>
  )
}

export default VoiceRecorder 